import { login, logout, getUserInfo as getUserInfoApi } from '@/api';
import { API_CODE } from '@/api/constants';

/**
 * 设置 Cookie（用于 EventSource 认证）
 * @param {string} name - Cookie 名称
 * @param {string} value - Cookie 值
 * @param {number} days - 过期天数
 */
function setCookie(name, value, days = 7) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * 删除 Cookie
 * @param {string} name - Cookie 名称
 */
function deleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * 用户状态管理
 * @namespace user
 */

const state = {
  // 从 localStorage 恢复 token 状态
  token: localStorage.getItem('token') || '',
  userInfo: null,
  roles: [],
  permissions: [],
  menuList: JSON.parse(localStorage.getItem('menuList') || '[]')
};

const mutations = {
  /**
   * 设置 Token
   * @param {Object} state - 状态对象
   * @param {string} token - 用户 Token
   */
  SET_TOKEN: (state, token) => {
    state.token = token;
  },

  /**
   * 设置用户信息
   * @param {Object} state - 状态对象
   * @param {Object} userInfo - 用户信息对象
   */
  SET_USER_INFO: (state, userInfo) => {
    state.userInfo = userInfo;
  },

  /**
   * 设置用户角色
   * @param {Object} state - 状态对象
   * @param {Array} roles - 角色列表
   */
  SET_ROLES: (state, roles) => {
    state.roles = roles;
  },

  /**
   * 设置用户权限
   * @param {Object} state - 状态对象
   * @param {Array} permissions - 权限列表
   */
  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions;
  },

  /**
   * 设置菜单列表
   * @param {Object} state - 状态对象
   * @param {Array} menuList - 菜单权限列表
   */
  SET_MENU_LIST: (state, menuList) => {
    state.menuList = menuList || [];
  },

  /**
   * 重置状态
   * @param {Object} state - 状态对象
   */
  RESET_STATE: (state) => {
    state.token = '';
    state.userInfo = null;
    state.roles = [];
    state.permissions = [];
    state.menuList = [];
  }
};

const actions = {
  /**
   * 用户登录
   * @param {Object} context - Vuex 上下文
   * @param {Object} context.commit - commit 方法
   * @param {Object} userInfo - 登录信息
   * @param {string} userInfo.phone - 手机号/登录名
   * @param {string} userInfo.password - 密码
   * @param {string} userInfo.verifyCode - 验证码
   * @param {string} userInfo.tempToken - 临时 Token
   * @returns {Promise<Object>} 登录结果
   */
  login({ commit }, userInfo) {
    return new Promise((resolve, reject) => {
      login(userInfo)
        .then(res => {
          if (res.code === API_CODE.SUCCESS && res.data && res.data.token) {
            const { token, id, username, menuList, avatarUrl, avatar } = res.data;
            commit('SET_TOKEN', token);
            commit('SET_MENU_LIST', menuList || []);
            localStorage.setItem('token', token);
            // 同时设置 Cookie，供 EventSource 使用
            setCookie('x-token', token, 7);
            localStorage.setItem('userId', id);
            localStorage.setItem('username', username);
            localStorage.setItem('menuList', JSON.stringify(menuList || []));
            
            // 如果登录返回了头像信息，也保存到 store 中；如果为空则不设置，el-avatar 会显示默认头像
            const userAvatar = avatarUrl || avatar;
            if (userAvatar) {
              commit('SET_USER_INFO', {
                id,
                username,
                avatar: userAvatar
              });
            }
            
            resolve(res);
          } else {
            reject(new Error(res.message || '登录失败'));
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * 获取用户信息
   * @param {Object} context - Vuex 上下文
   * @param {Object} context.commit - commit 方法
   * @returns {Promise<Object>} 用户信息
   */
  getUserInfo({ commit }) {
    return new Promise((resolve, reject) => {
      getUserInfoApi()
        .then(res => {
          if (res.code === API_CODE.SUCCESS && res.data) {
            const userInfo = res.data;
            // 将 avatarUrl 映射到 avatar 字段，如果为空则使用空字符串（el-avatar 会显示默认头像）
            const avatarValue = userInfo.avatarUrl || userInfo.avatar;
            if (avatarValue) {
              userInfo.avatar = avatarValue;
            } else {
              userInfo.avatar = '';
            }
            commit('SET_USER_INFO', userInfo);
            resolve(userInfo);
          } else {
            reject(new Error(res.message || '获取用户信息失败'));
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * 用户登出
   * @param {Object} context - Vuex 上下文
   * @param {Object} context.commit - commit 方法
   * @returns {Promise<void>}
   */
  logout({ commit }) {
    return new Promise((resolve, reject) => {
      logout()
        .then(() => {
          commit('RESET_STATE');
          localStorage.removeItem('token');
          // 同时删除 Cookie
          deleteCookie('x-token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('menuList');
          resolve();
        })
        .catch(error => {
          // 即使接口失败也清除本地状态
          commit('RESET_STATE');
          localStorage.removeItem('token');
          // 同时删除 Cookie
          deleteCookie('x-token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('menuList');
          reject(error);
        });
    });
  },

  /**
   * 重置 Token
   * @param {Object} context - Vuex 上下文
   * @param {Object} context.commit - commit 方法
   * @returns {Promise<void>}
   */
  resetToken({ commit }) {
    return new Promise((resolve) => {
      commit('RESET_STATE');
      resolve();
    });
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
